<template>
  <component
    :is="wrapperComponent"
    class="sp-quantum-select"
    :class="classModifiers"
    :open="menuOpen"
    :close-on-content-click="closeOnContentClick"
    :disabled="menuDisabled"
    :pointer="pointer"
    :mobile-expand-height-max="mobileExpandHeightMax"
    :location="location"
    :permanent="permanent"
    @update-open="handleMenuUpdateOpen"
  >
    <div v-if="!noMenu" slot="activator" class="sp-quantum-select__activator">
      <slot ref="activatorSlot" name="activator">
        <sp-button>Open menu</sp-button>
      </slot>
    </div>
    <div class="sp-quantum-select__content">
      <NavigationContainerShell
        :max-height="computedMaxHeight"
        :appearance="props.appearance"
        :title="props.title"
        :hide-title-bar="props.hideTitleBar"
        :on-back="containerOnBack"
      >
        <template #before-content>
          <slot name="before-content" />
        </template>
        <template #navbar-actions>
          <slot name="navbar-actions" />
        </template>
        <component
          :is="containerComponent"
          :key="containerResetKey"
          :aria-label="props.ariaLabel"
          :item-title="props.itemTitle"
          :item-value="props.itemValue"
          :item-subtitle="props.itemSubtitle"
          :item-tags="props.itemTags"
          :item-test-id="props.itemTestId"
          :input-test-id="props.inputTestId"
          :clearable="clearable"
          :listen-on-key-event="listenOnKeyEvent"
          :selected="model"
          :multiple="props.multiple"
          :checkboxes="props.checkboxes"
          :required="props.required"
          :selected-prepend-icon="props.selectedPrependIcon"
          :selected-prepend-icon-fill-color="props.selectedPrependIconFillColor"
          :hide-no-data="props.hideNoData"
          :hide-search-bar="effectiveHideSearchBar"
          :focus-search-on-open="focusSearchOnOpen"
          :no-filter="props.noFilter"
          :selectable-branches="props.selectableBranches"
          :branch-toggle-label="props.branchToggleLabel"
          :readonly="listReadonly"
          :open="menuOpen"
          :loading="loading"
          :placeholder="placeholder"
          :wrap="props.wrap"
          :search="props.search"
          :items="props.items"
          :max-results="props.maxResults"
          :fuzzy-search="props.fuzzySearch"
          :highlight-first-result="props.highlightFirstResult"
          :highlight-idle-first-result="props.highlightIdleFirstResult"
          :section-order="props.sectionOrder"
          :no-substring-fallback="props.noSubstringFallback"
          :exit-start="props.exitStart"
          :load-children="props.loadChildren"
          :initial-path="props.initialPath"
          :accordion-caret-variant="props.accordionCaretVariant"
          @update-selected="handleUpdateSelected"
          @update-search="handleUpdateSearch"
          @update-status="setResultStatus"
          @update-current-item="handleUpdateCurrentItem"
          @select-item="handleSelectItem"
          @search-enter="handleSearchEnter"
          @exit-start="handleExitStart"
          @update:on-back="setContainerOnBack"
        >
          <!-- The forwarded slot shadows the panel's own fallback, so the text is repeated here. -->
          <template #no-data>
            <slot name="no-data">{{ props.noDataText }}</slot>
          </template>
        </component>
      </NavigationContainerShell>
    </div>
  </component>

  <!-- Outside the wrapper on purpose. Slotted into the menu it would render through the popup or
       sheet, and a live region whose host is hidden when its text changes announces nothing. -->
  <div class="sp-quantum-select__status" role="status">{{ announcedStatus }}</div>
</template>

<script setup>
/**
 * QuantumSelect is an advanced web component designed to elevate the traditional select element into a highly
 * interactive and user-friendly interface.
 * It seamlessly integrates deep nested structure support, section headers, and a dynamic search input,
 * enabling users to effortlessly navigate and filter through complex datasets.
 *
 * @displayName QuantumSelect
 * @group Custom Elements
 * @component sp-quantum-select
 */

import { computed, nextTick, ref, toRef, useTemplateRef, watch } from "vue";
import { isNoSelection, isPresent, toArray, toBoolean } from "#ce/utils/props";
import { firstResultRow } from "./composables/useQuantumSelectItems/mark-first-result.js";
import { useExpose } from "#ce/composables/useExpose";
import { useHostElement } from "#ce/composables/useHostElement";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useSpList } from "#ce/components/SpList/composables/useSpList";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { t } from "#ce/shared/forms-i18n";
import { formAssociatedHost } from "#ce/framework/utils";
import { props as quantumSelectProps } from "./props.js";
import { useQuantumSelectPopupState } from "./composables/useQuantumSelectPopupState.js";
import { useSelectionModel } from "./composables/useSelectionModel.js";
import AccordionContainer from "./components/AccordionContainer.ce.vue";
import DrilldownContainer from "./components/DrilldownContainer.ce.vue";
import FlatContainer from "./components/FlatContainer.ce.vue";
import NavigationContainerShell from "./components/NavigationContainerShell.ce.vue";

const emit = defineEmits([
  "update-selected",
  "update-search",
  "update-open",
  "update-current-item",
  "update-selected-item",
  "search-enter",
  "exit-start",
]);

const props = defineProps(quantumSelectProps);

// sp-quantum-select is a genuine form control: it submits its selection, so it can stand alone in
// a form rather than only inside a host that owns the value.
// `name` is the host's own native member rather than a declared prop, so Vue must not treat it as
// template fallthrough — this root is a fragment, which has nowhere to put an inherited attribute.
defineOptions(formAssociatedHost({ inheritAttrs: false }));

// Disabled is governed by the form-control composable: presence-based own attribute plus ancestor
// <fieldset disabled>.
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

// A disabled picker holds no open intent, so `open` is refused while disabled rather than stored
// and honoured later, and `update-open` never reports an open that no picker showed.
const menuOpen = ref(toBoolean(props.open) && !disabled.value);

const host = useHostElement();

// Bumped on every menu open when `startOnRoot` is set. Vue tears down the
// container element and rebuilds it with a fresh frame stack, so the host never
// sees this mechanism, only the prop.
const containerResetKey = ref(0);

// Back-button callback surfaced by the active navigation container via
// `update:on-back`; passed through to the shell.
const containerOnBack = ref();

// One region for the whole picker. The drilldown remounts its panel per frame, and a live region
// inserted alongside the content it describes announces nothing, so the text is reported up here
// rather than rendered where it is computed.
const resultStatus = ref("");

function setResultStatus(event) {
  const [status] = event.detail ?? [event];
  resultStatus.value = status;
}

// Empty until the panel is on screen. A live region only announces a change, and a picker that
// mounted closed with results already in it would otherwise have nothing left to change on open.
const announcedStatus = computed(() => (listShown.value ? resultStatus.value : ""));

function setContainerOnBack(value) {
  containerOnBack.value = value;
}

function clearContainerOnBack() {
  containerOnBack.value = undefined;
}

function syncMenuOpenFromProp(value) {
  menuOpen.value = toBoolean(value) && !disabled.value;
}

function handleMenuOpenChange(value) {
  emit("update-open", value);
  if (value && toBoolean(props.startOnRoot)) {
    containerResetKey.value++;
    emit("update-current-item", null);
    // The reset remounts the panel, and with it the search bar the sheet's
    // `focus-on-open` pass targets; the replacement takes focus once rendered.
    if (focusSearchOnOpen.value) {
      nextTick(focusSearchBar);
    }
  }
}

function focusSearchBar() {
  host?.shadowRoot?.querySelector("sp-search-box")?.inputElement?.focus({ preventScroll: true });
}

watch(() => props.open, syncMenuOpenFromProp);
watch(disabled, (value) => value && (menuOpen.value = false));
watch(menuOpen, handleMenuOpenChange);

const listenOnKeyEvent = computed(() => toBoolean(props.listenOnKeyEvent) || menuOpen.value);

const noMenu = computed(() => toBoolean(props.noMenu));
const permanent = computed(() => toBoolean(props.permanent));
// The inline modes always show the list; the menu mode shows it while open.
const listShown = computed(() => noMenu.value || permanent.value || menuOpen.value);
const wrapperComponent = computed(() => (noMenu.value ? "div" : "sp-menu"));

const { isGtSmScreen } = useBreakpoints();

// True exactly when the wrapper renders as a bottom sheet — mirrors SpMenu's
// own `showBottomSheet = !permanent && !isGtSmScreen` (only the sp-menu wrapper
// has a sheet, so `noMenu` is excluded too).
const inSheetMode = computed(() => !noMenu.value && !permanent.value && !isGtSmScreen.value);

// Effective search-bar visibility passed to the container. `hideSearchBar`
// normally hides it everywhere, but a host that hides it because a slotted
// activator doubles as the search input can opt back in for sheet mode via
// `showSearchBarInSheet`: there the activator is covered by the sheet, so the
// user needs the in-list search bar to filter.
const effectiveHideSearchBar = computed(() => {
  if (!toBoolean(props.hideSearchBar)) {
    return false;
  }
  return !(toBoolean(props.showSearchBarInSheet) && inSheetMode.value);
});

// The sheet covers the slotted activator the user was typing in, so the
// in-list search bar it re-shows is where the typing continues.
const focusSearchOnOpen = computed(
  () => inSheetMode.value && toBoolean(props.hideSearchBar) && toBoolean(props.showSearchBarInSheet),
);

const containers = { drilldown: DrilldownContainer, flat: FlatContainer, accordion: AccordionContainer };
// Fall back to the drilldown container if `navigation` is an unsupported value:
// the prop validator only warns in dev, so without this `<component :is>` would
// receive `undefined` and render nothing in production.
const containerComponent = computed(() => containers[props.navigation] ?? DrilldownContainer);

// Switching navigation at runtime swaps the container; the new one is not
// required to emit `update:on-back`, so the previous callback would linger
// and leave a dead back button on the shell.
watch(containerComponent, clearContainerOnBack);
const computedMaxHeight = computed(() => {
  if (permanent.value && !props.maxHeight) {
    return "100%";
  }
  return props.maxHeight;
});

function handleMenuUpdateOpen({ detail }) {
  const [value] = detail;
  menuOpen.value = value;
}

const { getItemValue } = useSpList(props);

function closeMenuOnSelect() {
  if (toBoolean(props.closeOnSelect)) {
    menuOpen.value = false;
  }
}

const { model, selectByUser } = useSelectionModel(props, emit, { getItemValue, onChange: closeMenuOnSelect });

// The `selected` prop seeds the selection and names what a form reset restores; `value` is the
// live selection, in the shape `update-selected` carries.
const defaultSelected = toArray(props.selected);

// `toArray(undefined)` yields a one-entry array, so an unselected picker still has a member —
// the entries are what "selected" means here, not the array length.
const selectedValues = computed(() => model.value.map(getItemValue));
const selectedEntries = computed(() => selectedValues.value.filter((entry) => entry !== null && entry !== undefined));

const { exposeProperties } = useExpose();

exposeProperties({
  value: {
    configurable: true,
    get: () => selectedValues.value,
    set: (value) => {
      model.value = toArray(isNoSelection(value) ? [] : value);
    },
  },
  defaultValue: {
    configurable: true,
    get: () => defaultSelected,
  },
  // Read-only: whether the picker presents as a modal bottom sheet.
  inSheetMode: {
    configurable: true,
    get: () => inSheetMode.value,
  },
});

// The list lives in the shadow root, where the outer form cannot see it, so the element submits
// through ElementInternals — one entry per selection, the way a native <select multiple> does.
const { mirrorFormValue, mirrorValueMissing, onFormReset, name: formName } = useForm();

const formValue = computed(() => {
  if (!formName.value) {
    return null;
  }

  const data = new FormData();
  selectedEntries.value.forEach((entry) => data.append(formName.value, String(entry)));

  return data;
});

mirrorFormValue(formValue);

onFormReset(() => (model.value = toArray(defaultSelected)));

const classModifiers = computed(() => ({ "--disabled": disabled.value }));

// Null so the plain div wrapper of a `no-menu` picker carries no `disabled` attribute.
const menuDisabled = computed(() => (noMenu.value ? null : disabled.value || null));

// A disabled picker's rows must not be selectable, which is what sp-list's `readonly` gates.
const listReadonly = computed(() => toBoolean(props.readonly) || disabled.value);

// A required picker is satisfied only once something is selected.
const isRequired = computed(() => isPresent(props.required));
const validationMessage = computed(() => props.requiredMessage || t("select_value_missing"));

mirrorValueMissing(() => isRequired.value && selectedEntries.value.length === 0, validationMessage);

function handleUpdateSelected(value) {
  // A disabled picker must not mutate form state, even through a container whose items stay
  // clickable.
  if (disabled.value) {
    return;
  }

  selectByUser(value);
}

function handleUpdateSearch(value) {
  emit("update-search", value);
}

function handleUpdateCurrentItem(item) {
  emit("update-current-item", item);
}

// A terminal (leaf) item was selected. Surface the complete item object so
// consumers can route on the full payload (e.g. url / filters / value) — the
// component itself stays agnostic of what those fields mean. `update-selected`
// (value only) continues to fire independently for the simple binding case.
function handleSelectItem(item) {
  emit("update-selected-item", item);
}

// Enter in the panel's search box (the in-sheet search bar), surfaced so the
// host can commit its highlighted row.
function handleSearchEnter(event) {
  emit("search-enter", event);
}

// Arrow Up left the list past its first row (`exit-start` mode), surfaced so
// the host can take focus back.
function handleExitStart() {
  emit("exit-start");
}

// The activator is whatever the consumer slots, so the picker publishes the popup's state onto it
// rather than rendering a control of its own.
const activatorSlot = useTemplateRef("activatorSlot");
useQuantumSelectPopupState({
  items: () => props.items,
  // `listReadonly` folds in `disabled`, which gates selection but is a different axis from what the
  // popup is: a disabled combobox is the same control, unavailable.
  readonly: () => props.readonly,
  loading: () => props.loading,
  disabled,
  startOnRoot: () => props.startOnRoot,
  navigation: () => props.navigation,
  open: menuOpen,
  slot: activatorSlot,
  enabled: () => !noMenu.value && !permanent.value,
});

// Move roving focus to the open list's first / last option; no-op when the list
// isn't rendered (closed / no items).
const { exposeMethods } = useExpose();
exposeMethods({
  focusListItem(location) {
    host?.shadowRoot?.querySelector("sp-list")?.focusItem?.(location);
  },
  firstResult,
  // Commits `firstResult()` through the list's own user path, so it reports like a click. Returns
  // the item only when the list newly selected it: null while the picker is closed or disabled, on
  // a readonly list, for a row already selected, or with nothing committable. `markedOnly` commits
  // the highlighted row alone.
  selectFirstResult({ markedOnly = false } = {}) {
    if (disabled.value || !listShown.value) {
      return null;
    }

    const item = firstResult({ markedOnly });
    const committed = item != null && host?.shadowRoot?.querySelector("sp-list")?.selectItem?.(item);

    return committed ? item : null;
  },
});

// The rendered list's first result, read the same way `focusListItem` reaches the list. The row
// the highlight marked is honoured only when this picker's own prop produced the mark, so a
// consumer hand-marking rows for visuals does not steer what Enter commits.
function firstResult({ markedOnly = false } = {}) {
  const items = host?.shadowRoot?.querySelector("sp-list")?.items ?? [];

  return firstResultRow(items, { honourMarked: toBoolean(props.highlightFirstResult), markedOnly });
}
</script>

<style>
:host {
  display: block;
  --quantum-select-content-padding: var(--sp-comp-quantum-select-content-padding, 0.75rem);
  --quantum-select-content-padding-top: var(--sp-comp-quantum-select-content-padding-top, 0.5rem);
  --quantum-select-content-padding-bottom: var(--sp-comp-quantum-select-content-padding-bottom, 0.5rem);
  --quantum-select-popup-min-height: var(
    --sp-comp-quantum-select-popup-min-height,
    var(--sp-comp-popup-min-height, 25rem)
  );
  --quantum-select-popup-max-height: var(
    --sp-comp-quantum-select-popup-max-height,
    var(--quantum-select-popup-min-height)
  );

  --default-select-popup-width: 18.5rem;
  --quantum-select-popup-width: var(--sp-comp-quantum-select-popup-width, var(--default-select-popup-width));
  --quantum-select-animation-time: var(--sp-comp-quantum-select-animation-time, 0.3s);

  --sp-comp-divider-color: var(--sp-comp-select-divider-color, #ccc);
}

:host([no-menu]) {
  --default-select-popup-width: 100%;
  height: 100%;
}

:host([permanent]) {
  height: 100%;
}

/*
 * Only the full-height inline modes (permanent / no-menu, e.g. the required
 * filters modal) should stretch the inner wrapper to fill the host. In the
 * default menu/activator mode the wrapper must size to its content so the
 * activator can be vertically centered by the parent layout.
 */
:host([permanent]) .sp-quantum-select,
:host([no-menu]) .sp-quantum-select {
  height: 100%;
}
</style>

<style lang="scss" scoped>
.sp-quantum-select {
  height: auto;
}

.sp-quantum-select__content {
  position: relative;
  height: 100%;
}
.sp-quantum-select__status {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
